import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CreditCard, Loader2, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { InputField } from './InputField';
import { supabase } from '../../lib/supabase';

interface VatEnrichFieldProps {
  value: string;
  onChange: (value: string) => void;
  onEnrich: (data: EnrichResult) => void;
  error?: string;
  /** Label del campo (default "Partita IVA"; per l'extra-UE "Identificativo fiscale"). */
  label?: string;
  /** false = niente verifica VIES (paesi extra-UE): il campo resta un input normale. */
  viesEnabled?: boolean;
  /** true = barra compatta "P.IVA-first" (drawer): input inline con hint, senza label sopra. */
  compact?: boolean;
}

export interface EnrichResult {
  business_name?: string;
  street_address?: string;
  street_number?: string;
  postal_code?: string;
  city?: string;
  province?: string;
  country?: string;
}

type EnrichStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

export function VatEnrichField({ value, onChange, onEnrich, error, label = 'Partita IVA', viesEnabled = true, compact = false }: VatEnrichFieldProps) {
  const [status, setStatus] = useState<EnrichStatus>('idle');
  const [enrichData, setEnrichData] = useState<EnrichResult | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const lastCheckedRef = useRef<string>('');

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const checkVies = useCallback(async (vatRaw: string) => {
    const vat = vatRaw.replace(/[\s\-\.]/g, '');

    // Don't re-check the same value
    if (vat === lastCheckedRef.current) return;

    // Basic format check before calling
    const hasCountryPrefix = /^[A-Z]{2}/i.test(vat);
    const digits = hasCountryPrefix ? vat.slice(2) : vat;
    if (digits.length < 5) return;
    if (!hasCountryPrefix && !/^\d{11}$/.test(digits)) return;

    lastCheckedRef.current = vat;
    setStatus('loading');
    setShowDropdown(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('lead-enrich-preview', {
        body: { vat_number: vatRaw },
      });

      if (fnError || !data?.vies) {
        setStatus('error');
        return;
      }

      if (!data.vies.valid) {
        setStatus('not_found');
        setEnrichData(null);
        return;
      }

      const result: EnrichResult = {};
      if (data.vies.business_name) result.business_name = data.vies.business_name;
      if (data.vies.street_address) result.street_address = data.vies.street_address;
      if (data.vies.street_number) result.street_number = data.vies.street_number;
      if (data.vies.postal_code) result.postal_code = data.vies.postal_code;
      if (data.vies.city) result.city = data.vies.city;
      if (data.vies.province) result.province = data.vies.province;
      if (data.vies.country) result.country = data.vies.country;

      setEnrichData(result);
      setStatus('found');
    } catch {
      setStatus('error');
    }
  }, []);

  const handleChange = useCallback((val: string) => {
    onChange(val);

    if (!viesEnabled) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const clean = val.replace(/[\s\-\.]/g, '');
    const hasCountryPrefix = /^[A-Z]{2}/i.test(clean);
    const digits = hasCountryPrefix ? clean.slice(2) : clean;

    if (digits.length >= 11 || (hasCountryPrefix && digits.length >= 5)) {
      debounceRef.current = setTimeout(() => checkVies(val), 500);
    } else {
      setStatus('idle');
      setShowDropdown(false);
      lastCheckedRef.current = '';
    }
  }, [onChange, checkVies]);

  const handleApply = useCallback(() => {
    if (enrichData) {
      onEnrich(enrichData);
      setShowDropdown(false);
    }
  }, [enrichData, onEnrich]);

  return (
    <div className="relative" ref={dropdownRef}>
      {compact ? (
        <div>
          <div className="flex items-center gap-2 rounded border border-[rgba(55,53,47,0.16)] dark:border-white/15 bg-white dark:bg-white/[0.04] px-2.5 py-1.5 focus-within:border-[#2383E2] focus-within:ring-1 focus-within:ring-[#2383E2]">
            <CreditCard className="h-3.5 w-3.5 flex-shrink-0 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={label}
              aria-label={label}
              className="min-w-0 flex-1 bg-transparent text-[13px] text-[#37352F] dark:text-white placeholder-[rgba(55,53,47,0.4)] dark:placeholder-gray-500 focus:outline-none"
            />
            {status === 'loading' && <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-blue-500" />}
            {status === 'found' && !showDropdown && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />}
            {status === 'not_found' && <XCircle className="h-3.5 w-3.5 flex-shrink-0 text-red-400" />}
            {viesEnabled && status === 'idle' && !value && (
              <span className="hidden flex-shrink-0 whitespace-nowrap text-[12px] text-[rgba(55,53,47,0.4)] dark:text-gray-500 sm:inline">
                Compiliamo noi il resto
              </span>
            )}
          </div>
          {error && <p className="mt-0.5 text-[11px] text-red-500">{error}</p>}
        </div>
      ) : (
        <>
          <InputField
            label={label}
            value={value}
            onChange={handleChange}
            icon={CreditCard}
            placeholder={viesEnabled ? 'Inserisci partita IVA' : 'Es. CHE-116.281.710, EIN 84-3120991'}
            hint={viesEnabled && status === 'idle' ? 'Verifica automatica VIES (prefisso paese per l\'estero UE, es. DE811193231)' : ''}
            error={error}
          />

          {/* Status indicator */}
          {status === 'loading' && (
            <div className="absolute right-3 top-[38px] flex items-center">
              <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
            </div>
          )}
          {status === 'found' && !showDropdown && (
            <div className="absolute right-3 top-[38px] flex items-center">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </div>
          )}
          {status === 'not_found' && (
            <div className="absolute right-3 top-[38px] flex items-center">
              <XCircle className="h-4 w-4 text-red-400" />
            </div>
          )}
        </>
      )}

      {/* Dropdown */}
      {showDropdown && status !== 'idle' && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          {status === 'loading' && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifica VIES in corso...
            </div>
          )}

          {status === 'not_found' && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <XCircle className="h-4 w-4" />
              P.IVA non valida o non trovata nel registro VIES
            </div>
          )}

          {status === 'error' && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
              <XCircle className="h-4 w-4" />
              Servizio VIES non disponibile, riprova pi&ugrave; tardi
            </div>
          )}

          {status === 'found' && enrichData && (
            <button
              type="button"
              onClick={handleApply}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {enrichData.business_name || 'Azienda trovata'}
                    </span>
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                  </div>
                  {(enrichData.street_address || enrichData.city) && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {[enrichData.street_address, enrichData.street_number, enrichData.postal_code, enrichData.city, enrichData.province]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Clicca per compilare automaticamente i campi vuoti
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
